import SendFilled16 from '@carbon/icons-react/es/send--filled/16';
import { useBoolean } from 'ahooks';
import { Col } from 'antd';
import { isEmpty, pick } from 'lodash';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { PermissionSetType, User, UserType } from 'api/models';
import { CreatableSelect } from 'components/atoms/select';
import { toastService } from 'components/molecules/toast';
import { createAndInviteProjectMembersAction } from 'data/projects/actions';
import { useTranslation } from 'hooks/use-translation';
import { ExternalUser } from '..';
import { StyledButton, StyledRow, StyledSelect } from '../common-styles';
import { getFormattedUsersList, getOptions } from '../helpers';
import { useExternalInviteUserActions } from './use-external-user-invite-actions';

interface SelectedValue extends User {
  value: string;
  label: string;
}

const StyledCreatableSelect = styled(CreatableSelect)`
  ${StyledSelect};
`;

export const ExternalUserInvitation: FC<ExternalUser> = ({
  allOptions,
  inValidOptions,
  projectId,
  buttonText,
  autoFocus = false,
  company,
}) => {
  const dispatch = useDispatch();
  const [selectedUsers, setSelectedUsers] = useState<SelectedValue[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, { setTrue: disableSubmit, setFalse: enableSubmit }] =
    useBoolean(true);

  const [userInput, setUserInput] = useState('');

  const translatedTexts = {
    success: {
      title: useTranslation('invitation.success.title'),
      message: useTranslation('invitation.success.content'),
    },
    error: {
      title: useTranslation('invitation.team_member.error.title'),
      message: useTranslation('invitation.team_member.error.content'),
    },
  };

  const { isProjectMember, isNativeMember, isEmailValid, getNoOptionsMessage } =
    useExternalInviteUserActions({
      projectId,
      allOptions,
      inValidOptions,
    });

  const clearInputAndEnableSubmit = () => {
    setUserInput('');
    enableSubmit();
  };

  const handleSuccess = () => {
    toastService({
      toastContainer: {},
      title: translatedTexts.success.title,
      message: translatedTexts.success.message,
    }).success();

    setSelectedUsers([]);
    setIsLoading(false);
  };

  const handleFailure = () => {
    toastService({
      toastContainer: {},
      title: translatedTexts.error.title,
      message: translatedTexts.error.message,
    }).error();

    setIsLoading(false);
  };

  const handleInvite = () => {
    const invitingMembers = selectedUsers?.map((u) => {
      const permissionSetType = PermissionSetType.CustomerChampion;

      return {
        user: {
          emailId: u.value,
          company: pick(company, 'companyId'),
        },
        permissionSet: { permissionSetType },
      };
    });

    if (!isEmpty(selectedUsers)) {
      setIsLoading(true);
      dispatch(
        createAndInviteProjectMembersAction({
          projectId,
          data: invitingMembers,
          handleSuccess,
          handleFailure,
        }),
      );
    }
  };

  const handleInput = (input: string, seperator: string) => {
    const csv =
      input
        .split(seperator)
        .map((emailId) => {
          let standardisedEmailId = emailId.trim();

          /*
          When user tries to copy mail ids of all the guests from calendar, sometimes the mail id gets
          copied as " <_userName_> <_emailId_> ", example : John Doe <john@rocketlane.com>. To extract
          email from this specific format the following is done :
          */
          if (standardisedEmailId.includes('<')) {
            standardisedEmailId = standardisedEmailId
              .split('<')[1]
              .replace('>', '');
          }

          return standardisedEmailId;
        })
        .filter(Boolean) || [];
    const validInputs = csv.filter((csvItem) => isEmailValid(csvItem));

    if (!isEmpty(validInputs)) {
      const newUsersList = getFormattedUsersList([
        ...selectedUsers,
        ...validInputs.map((emailId) => ({
          label: emailId,
          value: emailId,
          company: { companyId: company.companyId },
        })),
      ]);

      setSelectedUsers(newUsersList);
    }

    clearInputAndEnableSubmit();
  };

  const handleInputChange = (input, action) => {
    if (action.action !== 'input-blur' && action.action !== 'menu-close') {
      if (input) {
        setUserInput(input.trim().toLowerCase());
        disableSubmit();
        if (input.includes(',')) {
          handleInput(input, ',');
        } else if (input.includes(' ')) {
          handleInput(input, ' ');
        }
      } else {
        clearInputAndEnableSubmit();
      }
    }
  };

  const handleNewUserCreation = (emailId: string) => {
    if (isEmailValid(emailId)) {
      const newUsersList = getFormattedUsersList([
        ...selectedUsers,
        {
          label: emailId,
          value: emailId,
          company: { companyId: company.companyId },
        },
      ]);

      setSelectedUsers(newUsersList);
      clearInputAndEnableSubmit();
    }
    if (isNativeMember(emailId) || isProjectMember(emailId)) {
      clearInputAndEnableSubmit();
    }
  };

  return (
    <StyledRow>
      <Col flex="auto">
        <StyledCreatableSelect
          type="user"
          isMulti
          isClearable
          autoFocus={autoFocus}
          placeholder={<FormattedMessage id="invitation.help.name_or_email" />}
          options={getFormattedUsersList(
            getOptions(allOptions, inValidOptions, UserType.External),
          )}
          value={getFormattedUsersList([...selectedUsers])}
          inputValue={userInput}
          formatCreateLabel={() => (
            <FormattedMessage id="invitation.create.placeholder" />
          )}
          onBlur={(e) => handleNewUserCreation(e.target.value)}
          onInputChange={handleInputChange}
          noOptionsMessage={getNoOptionsMessage}
          onCreateOption={(emailId) => handleNewUserCreation(emailId)}
          onChange={
            (newValue: User | User[] | null | undefined) =>
              setSelectedUsers(
                isEmpty(newValue) ? [] : getFormattedUsersList(newValue),
              ) // to handle null or undefined values
          }
          isValidNewOption={(input) => isEmailValid(input.trim())}
          closeMenuOnSelect={false}
        />
      </Col>
      <Col flex="none">
        <StyledButton
          data-cy="externalMembers.invite"
          type="primary"
          htmlType="submit"
          icon={<SendFilled16 />}
          iconPlacement="right"
          onClick={handleInvite}
          loading={isLoading}
          disabled={isEmpty(selectedUsers) || isDisabled}
        >
          {buttonText}
        </StyledButton>
      </Col>
    </StyledRow>
  );
};
